<script setup>
import {
  ref,
  computed,
  h,
  shallowRef,
  toRaw,
  useTemplateRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useOperators } from 'dashboard/components-next/filter/operators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SidePanel from 'dashboard/components-next/side-panel/SidePanel.vue';
import {
  generateAutomationPayload,
  getActionIcon,
  getAttributes,
  getDefaultActions,
  getDefaultConditions,
  getSelectedBoardId,
  getSelectedBoardIdFromActions,
  isActionBeforeTransfer,
  KANBAN_EVENTS,
} from 'dashboard/helper/automationHelper';
import { getAttributeIcon } from 'dashboard/components-next/filter/helper/filterAttributeIcons';
import { provideDropdownTeleport } from 'dashboard/components-next/dropdown-menu/base/provider';
import { validateAutomation } from 'dashboard/helper/validations';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';
import {
  AUTOMATION_RULE_EVENTS,
  AUTOMATION_ACTION_TYPES,
  AUTOMATION_RUN_TYPES,
  DEFAULT_DELAY_MINUTES,
  KANBAN_EVENT_ACTIONS,
} from './constants';
import {
  ACTIONS_BY_SUBJECT,
  ALL_DATE_TRIGGER_ACTIONS,
  CONDITION_EVENT_BY_SUBJECT,
  DATE_ONLY_ACTIONS,
  DATE_REACHED_EVENT,
  MESSAGE_ACTIONS,
  OFFER_ACTION,
  SUBJECT_BY_SOURCE,
  buildDateTriggerTemplate,
  buildDefaultDateTrigger,
  isCrmModuleEnabled,
  isOfferParamsValid,
  validateDateTrigger,
} from './dateTrigger';
import AutomationRunTypeSelector from './components/AutomationRunTypeSelector.vue';
import AutomationWaitCondition from './components/AutomationWaitCondition.vue';
import AutomationInstantTrigger from './components/AutomationInstantTrigger.vue';
import AutomationDateTrigger from './components/AutomationDateTrigger.vue';
import AutomationActions from './components/AutomationActions.vue';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['create', 'edit'].includes(value),
  },
  automationTypes: {
    type: Object,
    required: true,
  },
  getConditionDropdownValues: {
    type: Function,
    required: true,
  },
  getActionDropdownValues: {
    type: Function,
    required: true,
  },
  appendNewCondition: {
    type: Function,
    required: true,
  },
  appendNewAction: {
    type: Function,
    required: true,
  },
  removeFilter: {
    type: Function,
    required: true,
  },
  removeAction: {
    type: Function,
    required: true,
  },
  resetAction: {
    type: Function,
    required: true,
  },
  onEventChange: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(['save']);
const automation = defineModel('automation', { type: Object, default: null });

const INPUT_TYPE_MAP = {
  multi_select: 'multiSelect',
  search_select: 'searchSelect',
  plain_text: 'plainText',
  multi_text: 'multiText',
  date: 'date',
};

const { t } = useI18n();
const { isCloudFeatureEnabled, currentAccount } = useAccount();
const { operators } = useOperators();

provideDropdownTeleport();

const panelRef = ref(null);
const instantTriggerRef = useTemplateRef('instantTriggerRef');
const waitConditionRef = useTemplateRef('waitConditionRef');
const dateTriggerRef = useTemplateRef('dateTriggerRef');
const errors = ref({});

const isEditMode = computed(() => props.mode === 'edit');

const allowsDelayedExecution = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.DELAYED_AUTOMATIONS)
);

// Event a rule falls back to when it leaves "on a date" with no instant draft to restore.
const DEFAULT_INSTANT_EVENT = 'conversation_created';

// The wait lives here rather than in the wait section so that switching between the run
// types doesn't discard a duration the user already typed in.
const runType = ref(AUTOMATION_RUN_TYPES.INSTANT);
const isSavedWait = ref(false);
const delayMinutes = ref(DEFAULT_DELAY_MINUTES);
const delayUnit = ref(DURATION_UNITS.HOURS);
// Trigger drafts keyed by run type, so switching modes back and forth keeps what was typed.
const triggerDrafts = shallowRef({});
const isSyncingRunType = ref(false);
// Bumped on every open() so the wait and date sections remount and re-read the rule they are given.
const waitSectionKey = ref(0);

const isDelayed = computed(() => runType.value === AUTOMATION_RUN_TYPES.WAIT);
const isDateTrigger = computed(
  () => runType.value === AUTOMATION_RUN_TYPES.DATE
);

const dateSubject = computed(() =>
  isDateTrigger.value
    ? SUBJECT_BY_SOURCE[automation.value?.date_trigger?.source_type] || null
    : null
);

const isCrmEnabled = computed(() => isCrmModuleEnabled(currentAccount.value));

// Without a source yet the action picker offers every action some date target accepts.
// The WhatsApp offer also needs the Clientes (CRM) module.
const allowedDateActions = computed(() =>
  (dateSubject.value
    ? ACTIONS_BY_SUBJECT[dateSubject.value]
    : ALL_DATE_TRIGGER_ACTIONS
  ).filter(key => key !== OFFER_ACTION || isCrmEnabled.value)
);

// The offer carries its own opt-out legend; the other message actions get this one.
const actionHint = action =>
  isDateTrigger.value &&
  action.action_name !== OFFER_ACTION &&
  MESSAGE_ACTIONS.includes(action.action_name)
    ? t('AUTOMATION.DATE_TRIGGER.OFFER.OPT_OUT_HINT')
    : '';

const executionDelayInvalid = computed(
  () => isDelayed.value && !Number.isFinite(delayMinutes.value)
);

const statusOptions = computed(() =>
  (props.getConditionDropdownValues('status') || [])
    .filter(option => option.id !== 'all')
    .map(option => ({ value: option.id, label: option.name }))
);

const inboxOptions = computed(
  () => props.getConditionDropdownValues('inbox_id') || []
);

const cloneConditions = conditions => JSON.parse(JSON.stringify(conditions));

const captureTriggerDraft = () => {
  if (!automation.value) return null;

  return {
    eventName: automation.value.event_name,
    delayTrigger: automation.value.execution_delay_trigger,
    conditions: cloneConditions(automation.value.conditions),
    dateTrigger: automation.value.date_trigger
      ? structuredClone(toRaw(automation.value.date_trigger))
      : null,
  };
};

const restoreTriggerDraft = draft => {
  if (!automation.value || !draft) return;

  automation.value.event_name = draft.eventName;
  automation.value.execution_delay_trigger = draft.delayTrigger;
  automation.value.conditions = cloneConditions(draft.conditions);
  if (draft.dateTrigger) {
    automation.value.date_trigger = structuredClone(draft.dateTrigger);
  }
};

const applyDefaultTrigger = nextRunType => {
  if (!automation.value) return;

  if (nextRunType === AUTOMATION_RUN_TYPES.DATE) {
    automation.value.event_name = DATE_REACHED_EVENT;
    automation.value.conditions = [];
    automation.value.date_trigger = buildDefaultDateTrigger();
    return;
  }
  // Leaving "on a date" with nothing to restore: date_reached means nothing to the other modes
  // (the wait section still applies its own trigger when it mounts).
  if (automation.value.event_name === DATE_REACHED_EVENT) {
    automation.value.event_name = DEFAULT_INSTANT_EVENT;
    automation.value.conditions = getDefaultConditions(DEFAULT_INSTANT_EVENT);
  }
};

// Drops actions the current mode or date target can't run; an emptied list gets the default action.
const keepAllowedActions = isAllowed => {
  if (!automation.value) return;

  const allowedActions = automation.value.actions.filter(action =>
    isAllowed(action.action_name)
  );
  if (allowedActions.length === automation.value.actions.length) return;
  automation.value.actions = allowedActions.length
    ? allowedActions
    : getDefaultActions();
};

// Show the wait in the largest whole unit (240 min → 4 hours). The delay and the date trigger are
// passed in by open() rather than read from `automation`, whose model prop only settles a tick later.
const syncRunTypeState = (executionDelay, dateTrigger) => {
  isSyncingRunType.value = true;
  if (dateTrigger) runType.value = AUTOMATION_RUN_TYPES.DATE;
  else if (executionDelay) runType.value = AUTOMATION_RUN_TYPES.WAIT;
  else runType.value = AUTOMATION_RUN_TYPES.INSTANT;
  isSyncingRunType.value = false;
  isSavedWait.value = isEditMode.value && Boolean(executionDelay);
  const minutes = executionDelay || DEFAULT_DELAY_MINUTES;
  if (minutes % 1440 === 0) delayUnit.value = DURATION_UNITS.DAYS;
  else if (minutes % 60 === 0) delayUnit.value = DURATION_UNITS.HOURS;
  else delayUnit.value = DURATION_UNITS.MINUTES;
  delayMinutes.value = minutes;
  waitSectionKey.value += 1;

  // Drafts are captured when the run type actually changes, so they start empty: seeding them
  // here would read `automation` before its model prop settles and keep the previous rule.
  triggerDrafts.value = {};
};

watch(
  runType,
  (nextRunType, previousRunType) => {
    if (isSyncingRunType.value) return;

    triggerDrafts.value = {
      ...triggerDrafts.value,
      [previousRunType]: captureTriggerDraft(),
    };
    const nextDraft = triggerDrafts.value[nextRunType];
    if (nextDraft) restoreTriggerDraft(nextDraft);
    else applyDefaultTrigger(nextRunType);

    if (nextRunType === AUTOMATION_RUN_TYPES.DATE) {
      keepAllowedActions(key => allowedDateActions.value.includes(key));
    } else {
      keepAllowedActions(key => !DATE_ONLY_ACTIONS.includes(key));
    }
    // A restored in-form wait draft must hydrate like a saved wait instead of being reset on mount.
    isSavedWait.value =
      nextRunType === AUTOMATION_RUN_TYPES.WAIT && Boolean(nextDraft);
  },
  { flush: 'sync' }
);

// Picking a reference date of another target narrows the actions to that target's catalog.
watch(dateSubject, nextSubject => {
  if (!nextSubject) return;
  keepAllowedActions(key => allowedDateActions.value.includes(key));
});

watch([isDelayed, delayMinutes], () => {
  if (!automation.value || !allowsDelayedExecution.value) return;
  automation.value.execution_delay = isDelayed.value
    ? delayMinutes.value
    : null;
  // The trigger describes how a wait is anchored, so an instant rule carries none.
  if (!isDelayed.value) automation.value.execution_delay_trigger = null;
});

const titleKey = computed(() =>
  isEditMode.value ? 'AUTOMATION.EDIT.TITLE' : 'AUTOMATION.ADD.TITLE'
);
const cancelKey = computed(() =>
  isEditMode.value
    ? 'AUTOMATION.EDIT.CANCEL_BUTTON_TEXT'
    : 'AUTOMATION.ADD.CANCEL_BUTTON_TEXT'
);
const submitKey = computed(() =>
  isEditMode.value ? 'AUTOMATION.EDIT.SUBMIT' : 'AUTOMATION.ADD.SUBMIT'
);

const getTranslatedAttributes = (type, event) => {
  return getAttributes(type, event).map(attribute => {
    const skipTranslation =
      attribute.customAttributeType ||
      [
        'contact_custom_attribute',
        'conversation_custom_attribute',
        'task_custom_attribute',
      ].includes(attribute.key);
    return {
      ...attribute,
      name: skipTranslation
        ? attribute.name
        : t(`AUTOMATION.ATTRIBUTES.${attribute.name}`),
    };
  });
};

const eventName = computed(() => automation.value?.event_name);

// "On a date" has no event of its own: conditions come from the target's catalog
// (conversation_updated for a conversation, kanban_task_overdue for a task, none for a contact).
const conditionEventName = computed(() =>
  isDateTrigger.value
    ? CONDITION_EVENT_BY_SUBJECT[dateSubject.value] || null
    : eventName.value
);

const isKanbanEvent = computed(() =>
  KANBAN_EVENTS.includes(conditionEventName.value)
);

const selectedBoardId = computed(() =>
  getSelectedBoardId(automation.value?.conditions)
);

const selectedBoardIdFromActions = computed(() =>
  getSelectedBoardIdFromActions(automation.value?.actions)
);

const hasBoardSelected = computed(
  () => !!(selectedBoardId.value || selectedBoardIdFromActions.value)
);

const showConditionBoardWarning = condition => {
  return (
    condition.attribute_key === 'kanban_step_id' &&
    !automation.value?.conditions?.some(
      c => c.attribute_key === 'kanban_board_id' && c.values
    )
  );
};

const showActionBoardWarning = action => {
  return action.action_name === 'move_to_step' && !hasBoardSelected.value;
};

// Funnel-dependent action placed before a transfer that would override it.
const showActionOrderWarning = index =>
  isActionBeforeTransfer(automation.value?.actions, index);

const filterTypes = computed(() => {
  const event = conditionEventName.value;
  if (!event || !props.automationTypes[event]) return [];

  const attributes = getTranslatedAttributes(props.automationTypes, event);

  // Filter out duplicate kanban_board_id if one already exists in conditions
  const hasBoardCondition = automation.value?.conditions?.some(
    c => c.attribute_key === 'kanban_board_id'
  );

  return attributes
    .filter(attr => {
      if (attr.key === 'kanban_board_id' && hasBoardCondition) {
        // Keep it only if it's the currently selected attribute
        return automation.value?.conditions?.some(
          c => c.attribute_key === 'kanban_board_id'
        );
      }
      return true;
    })
    .map(attr => {
      if (attr.disabled) {
        return { value: attr.key, label: attr.name, disabled: true };
      }

      const mappedInputType = INPUT_TYPE_MAP[attr.inputType] || 'plainText';
      const options =
        props.getConditionDropdownValues(
          attr.key,
          automation.value?.conditions || []
        ) || [];

      const filterOperators = (attr.filterOperators || []).map(op => {
        const enriched = operators.value[op.value];
        if (enriched) return enriched;
        return {
          value: op.value,
          label: t(`FILTER.OPERATOR_LABELS.${op.value}`),
          hasInput: true,
          inputOverride: null,
          icon: h('span', { class: 'i-ph-equals-bold !text-n-blue-11' }),
        };
      });

      return {
        attributeKey: attr.key,
        value: attr.key,
        attributeName: attr.name,
        icon: getAttributeIcon({
          attributeKey: attr.key,
          attributeDisplayType: attr.attributeDisplayType,
        }),
        label: attr.name,
        inputType: mappedInputType,
        options,
        filterOperators,
        dataType: 'text',
        attributeModel: attr.customAttributeType || 'standard',
      };
    });
});

const automationRuleEvents = computed(() =>
  AUTOMATION_RULE_EVENTS.map(event => ({
    ...event,
    value: t(`AUTOMATION.EVENTS.${event.value}`),
  }))
);

const hasAutomationMutated = computed(() => {
  return Boolean(
    automation.value?.conditions[0]?.values ||
      automation.value?.actions[0]?.action_params?.length
  );
});

const automationActionTypes = computed(() => {
  const actionTypes = isCloudFeatureEnabled('sla')
    ? AUTOMATION_ACTION_TYPES
    : AUTOMATION_ACTION_TYPES.filter(({ key }) => key !== 'add_sla');

  return actionTypes
    .filter(action =>
      isDateTrigger.value
        ? allowedDateActions.value.includes(action.key)
        : !DATE_ONLY_ACTIONS.includes(action.key)
    )
    .filter(
      action =>
        !KANBAN_EVENTS.includes(eventName.value) ||
        KANBAN_EVENT_ACTIONS.includes(action.key)
    )
    .filter(
      action =>
        action.key !== 'start_astra_flow' ||
        ['kanban_task_created', 'kanban_task_updated'].includes(eventName.value)
    )
    .map(action => ({
      ...action,
      label: t(`AUTOMATION.ACTIONS.${action.label}`),
      icon: getActionIcon(action.key),
    }));
});

watch(
  () => automation.value,
  () => {
    if (Object.keys(errors.value).length) {
      errors.value = {};
    }
  },
  { deep: true }
);

const TRIGGER_REFS = {
  [AUTOMATION_RUN_TYPES.INSTANT]: instantTriggerRef,
  [AUTOMATION_RUN_TYPES.WAIT]: waitConditionRef,
  [AUTOMATION_RUN_TYPES.DATE]: dateTriggerRef,
};

const isConditionsValid = () =>
  TRIGGER_REFS[runType.value].value?.validate() ?? true;

const resetValidation = () => {
  errors.value = {};
  Object.values(TRIGGER_REFS).forEach(triggerRef =>
    triggerRef.value?.resetValidation()
  );
};

const syncCustomAttributeTypes = () => {
  automation.value.conditions.forEach(condition => {
    const filterType = filterTypes.value.find(
      ft => ft.attributeKey === condition.attribute_key
    );
    condition.custom_attribute_type =
      filterType?.attributeModel === 'standard'
        ? ''
        : filterType?.attributeModel || '';
  });
};

// The simulation reads the form as it is now; a saved rule also sends its id so the server can
// tell what already ran (already_executed). Nothing is saved.
const buildSimulationPayload = () => {
  syncCustomAttributeTypes();
  const payload = generateAutomationPayload(automation.value);
  if (isEditMode.value && automation.value.id) {
    payload.automation_rule_id = automation.value.id;
  }
  return payload;
};

// A template replaces the trigger and the actions; the date section remounts to read it.
const applyDateTemplate = templateKey => {
  const { date_trigger: dateTrigger, actions } =
    buildDateTriggerTemplate(templateKey);
  automation.value.date_trigger = dateTrigger;
  automation.value.actions = actions;
  automation.value.conditions = [];
  if (!automation.value.name) {
    automation.value.name = t(
      `AUTOMATION.DATE_TRIGGER.TEMPLATES.${templateKey.toUpperCase()}.NAME`
    );
  }
  waitSectionKey.value += 1;
};

const open = (executionDelay = null, dateTrigger = null) => {
  resetValidation();
  syncRunTypeState(executionDelay, dateTrigger);
  panelRef.value?.open();
};

const close = () => {
  resetValidation();
  panelRef.value?.close();
};

const emitSaveAutomation = () => {
  syncCustomAttributeTypes();
  const conditionsValid = isConditionsValid();
  errors.value = validateAutomation(automation.value);
  if (isDateTrigger.value) {
    // Conditions are optional on a date rule (a contact target has none).
    delete errors.value.conditions;
    Object.assign(
      errors.value,
      validateDateTrigger(automation.value.date_trigger)
    );
    automation.value.actions.forEach((action, index) => {
      if (
        action.action_name === OFFER_ACTION &&
        !isOfferParamsValid(action.action_params)
      ) {
        errors.value[`action_${index}`] = 'ACTION_PARAMETERS_REQUIRED';
      }
    });
  }
  if (allowsDelayedExecution.value && executionDelayInvalid.value) {
    errors.value.execution_delay = true;
  }
  if (Object.keys(errors.value).length === 0 && conditionsValid) {
    const payload = generateAutomationPayload(automation.value);
    // The API rejects the param when the feature is off; existing values are kept server-side.
    if (!allowsDelayedExecution.value) {
      delete payload.execution_delay;
      delete payload.execution_delay_trigger;
    }
    emit('save', payload, props.mode);
  }
};

defineExpose({ open, close });
</script>

<template>
  <SidePanel ref="panelRef" width="3xl" :title="$t(titleKey)">
    <div v-if="automation" class="flex flex-col w-full gap-6">
      <div class="flex flex-col">
        <woot-input
          v-model="automation.name"
          :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
          type="text"
          :class="{ error: errors.name }"
          :error="errors.name ? $t('AUTOMATION.ADD.FORM.NAME.ERROR') : ''"
          :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
        />
        <woot-input
          v-model="automation.description"
          :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
          type="text"
          :class="{ error: errors.description }"
          :error="
            errors.description ? $t('AUTOMATION.ADD.FORM.DESC.ERROR') : ''
          "
          :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
        />
      </div>
      <AutomationRunTypeSelector
        v-model="runType"
        :allows-wait="allowsDelayedExecution"
      />
      <AutomationDateTrigger
        v-if="isDateTrigger"
        :key="waitSectionKey"
        ref="dateTriggerRef"
        v-model:date-trigger="automation.date_trigger"
        v-model:conditions="automation.conditions"
        :filter-types="filterTypes"
        :errors="errors"
        :show-condition-board-warning="showConditionBoardWarning"
        :get-simulation-payload="buildSimulationPayload"
        :show-templates="!isEditMode"
        @apply-template="applyDateTemplate"
      />
      <AutomationWaitCondition
        v-else-if="isDelayed"
        :key="waitSectionKey"
        ref="waitConditionRef"
        v-model:event-name="automation.event_name"
        v-model:conditions="automation.conditions"
        v-model:delay-trigger="automation.execution_delay_trigger"
        v-model:delay="delayMinutes"
        v-model:unit="delayUnit"
        :status-options="statusOptions"
        :inbox-options="inboxOptions"
        :filter-types="filterTypes"
        :remove-filter="removeFilter"
        :is-saved-wait="isSavedWait"
        :has-error="Boolean(errors.execution_delay)"
      />
      <AutomationInstantTrigger
        v-else
        ref="instantTriggerRef"
        v-model:event-name="automation.event_name"
        v-model:conditions="automation.conditions"
        :events="automationRuleEvents"
        :filter-types="filterTypes"
        :show-condition-board-warning="showConditionBoardWarning"
        :errors="errors"
        :show-reset-message="!isEditMode && hasAutomationMutated"
        :append-new-condition="appendNewCondition"
        :remove-filter="removeFilter"
        :on-event-change="onEventChange"
      />
      <AutomationActions
        v-model="automation.actions"
        :action-types="automationActionTypes"
        :selected-board-id="selectedBoardId || selectedBoardIdFromActions"
        :is-kanban-event="isKanbanEvent"
        :show-action-board-warning="showActionBoardWarning"
        :show-action-order-warning="showActionOrderWarning"
        :action-hint="actionHint"
        :conditions="automation.conditions"
        :get-action-dropdown-values="getActionDropdownValues"
        :files="automation.files"
        :show-file-name="isEditMode"
        :errors="errors"
        :append-new-action="appendNewAction"
        :remove-action="removeAction"
        :reset-action="resetAction"
      />
    </div>
    <template #footer>
      <div class="flex flex-row justify-end w-full gap-2">
        <NextButton
          faded
          slate
          type="button"
          :label="$t(cancelKey)"
          @click="close"
        />
        <NextButton
          solid
          blue
          type="button"
          :label="$t(submitKey)"
          @click="emitSaveAutomation"
        />
      </div>
    </template>
  </SidePanel>
</template>
